import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp, router } from "@inertiajs/react"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

const appName = import.meta.env.VITE_APP_NAME ?? "Rebulk"

// Suppress Inertia's default error modal in production. When a partial
// reload (router.reload({ only: [...] }) from a page's
// mount/visibility/cable handlers) races against session expiry, a deleted
// resource, or an org-context change, Inertia receives a non-JSON response
// (auth redirect, 404 HTML, etc.) and pops the modal — confusing for users
// and not actionable. Log instead so we can still diagnose. Dev keeps the
// modal so developers see real server responses when things break locally.
router.on("invalid", (event) => {
  if (import.meta.env.PROD) {
    event.preventDefault()
  }
  const response = event.detail.response
  const summary = {
    status: response?.status,
    statusText: response?.statusText,
    url: response?.config?.url,
    message:
      typeof response?.data?.message === "string"
        ? response.data.message
        : undefined,
    timestamp: new Date().toISOString(),
  }
  if (import.meta.env.PROD) {
    console.error("[Inertia invalid response]", summary)
  } else {
    console.error("[Inertia invalid response]", summary, response)
  }
})

router.on("exception", (event) => {
  const err = event.detail.exception
  if (import.meta.env.PROD) {
    console.error("[Inertia exception]", {
      name: err?.name,
      message: err?.message,
    })
  } else {
    console.error("[Inertia exception]", err)
  }
})

void createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/**/*.tsx",
      { eager: true },
    )
    const page = pages[`../pages/${name}.tsx`]

    if (!page) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    page.default.layout ??= [PersistentLayout]
    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

initializeTheme()
